/**
 * useRadioAvailable — lightweight probe for the radio WebSocket server.
 *
 * Tries to connect to ws://localhost:8765 every 10s.
 * Returns true/false so the UI can grey out the Radio button.
 * Does NOT keep the connection open — just a quick connect/close probe.
 */

import { useState, useEffect, useRef } from 'react'

const WS_URL = 'ws://localhost:8765'
const PROBE_INTERVAL = 10_000

export function useRadioAvailable(): boolean {
  const [available, setAvailable] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    function probe() {
      try {
        const ws = new WebSocket(WS_URL)
        const timeout = setTimeout(() => {
          ws.close()
          if (mountedRef.current) setAvailable(false)
        }, 2000)

        ws.onopen = () => {
          clearTimeout(timeout)
          ws.close()
          if (mountedRef.current) setAvailable(true)
        }

        ws.onerror = () => {
          clearTimeout(timeout)
          if (mountedRef.current) setAvailable(false)
        }
      } catch {
        if (mountedRef.current) setAvailable(false)
      }
    }

    probe()
    const interval = setInterval(probe, PROBE_INTERVAL)

    return () => {
      mountedRef.current = false
      clearInterval(interval)
    }
  }, [])

  return available
}
